// Libs
import React, { useState, useContext, useEffect } from "react";
import { useObserver, useRouter, useMediaQuery } from "@shoothill/core";
import { Box, Typography, Link, FormControl, Radio, RadioGroup, FormControlLabel, LinearProgress } from "@material-ui/core";
import { RadioSVG } from "Globals/Views/IconSVGs/RadioSVG";
import { RadioCheckedSVG } from "Globals/Views/IconSVGs/RadioCheckedSVG";
import { StoresContext, Stores } from "Globals/Stores";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { StyledButton } from "Globals/Styles/ButtonStyles";

//Models
import { ApplicationStepEnum } from "Globals/Models/ApplicationStepEnum";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { Section } from "Globals/Models/Section";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";

// Views
import { StepContainer } from "Views/Application/FormSections/StepContainer";
import { DeleteApplicantModal } from "Views/Application/Modal/DeleteApplicantModal";
import { SaveView } from "Views/Application/SaveView";
import { SaveForLaterView } from "Views/Application/SaveForLaterView";
import { PersonalDetailsView } from "Views/Application/FormSections/PersonalDetails/PersonalDetailsView";
import { EmploymentDetailsView } from "Views/Application/FormSections/EmploymentDetails/EmploymentDetailsView";
import { IncomeDetailsView } from "Views/Application/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsView";
import { StatementDetailsView } from "Views/Application/FormSections/StatementDetails/StatementDetailsView";
import { PropertyDetailsView } from "Views/Application/FormSections/PropertyDetails/PropertyDetailsView";
import { CurrentPropertyDetailsView } from "Views/Application/FormSections/CurrentPropertyDetails/CurrentPropertyDetailsView";
import { NewPropertyDetailsView } from "Views/Application/FormSections/NewPropertyDetails/NewPropertyDetailsView";
import { BackgroundInformationView } from "Views/Application/FormSections/BackgroundInformation/BackgroundInformationView";
import { DeclarationView } from "./FormSections/Declaration/DeclarationView";
import { CreditReportView } from "./FormSections/CreditReport/CreditReportView";
import { SupportingInformationView } from "./FormSections/SupportingInformation/SupportingInformationView";
const saveStore: SaveStore = StoresInstance.Domain.SaveStore;
interface IProps {
    viewModel: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}
export interface MatchParams {
    applicationId?: string;
}

export interface IAccordionControls {
    handleChange: (panel: FormSectionsEnum) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
    expanded: FormSectionsEnum | false;
    setExpanded: React.Dispatch<React.SetStateAction<FormSectionsEnum | false>>;
}

export const ApplicationFormView: React.FC = () => {
    const { match } = useRouter();
    const { applicationId } = match.params as MatchParams;
    const [viewModel] = useState(() => new ApplicationFormViewModel(applicationId));
    const [tempApplicantNumber, setTempApplicantNumber] = useState(1);
    const [modalStatus, setModalStatus] = useState(false);
    const storesContext = useContext<Stores>(StoresContext);

    const [currentStep, setCurrentStep] = useState<ApplicationStepEnum>(ApplicationStepEnum.Step1);
    const [showStep1ProceedButton, setShowStep1ProceedButton] = useState(true);
    const [showStep2ProceedButton, setShowStep2ProceedButton] = useState(true);

    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;

    // Accordion Controls
    const [expanded, setExpanded] = React.useState<FormSectionsEnum | false>(false);

    const handleChange = (panel: FormSectionsEnum) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        storesContext.Domain.SaveStore.saveBySection(panel);
        setExpanded(isExpanded ? panel : false);
        setShowMsgOne(false);
        setShowMsgTwo(false);
    };

    let accordionControls: IAccordionControls = {
        handleChange,
        expanded,
        setExpanded,
    };

    const personalDetails: Section = {
        title: "Personal details",
        formSection: FormSectionsEnum.PersonalDetails,
        component: PersonalDetailsView,
    };
    const employmentDetails: Section = {
        title: "Your employment Details",
        formSection: FormSectionsEnum.EmploymentDetails,
        component: EmploymentDetailsView,
    };
    const incomeSavingsAndOutgoings: Section = {
        title: "Your income, savings and outgoings",
        formSection: FormSectionsEnum.IncomeDetails,
        component: IncomeDetailsView,
    };
    const statements: Section = {
        title: "Statements",
        formSection: FormSectionsEnum.StatementDetails,
        component: StatementDetailsView,
    };
    const propertyDetails: Section = {
        title: "Where you live now and your requirements",
        formSection: FormSectionsEnum.PropertyDetails,
        component: PropertyDetailsView,
    };
    const currentPropertyDetails: Section = {
        title: "Current / previous home owners",
        formSection: FormSectionsEnum.CurrentPropertyDetails,
        component: CurrentPropertyDetailsView,
    };
    const newPropertyDetails: Section = {
        title: "New property details",
        formSection: FormSectionsEnum.NewPropertyDetails,
        component: NewPropertyDetailsView,
    };
    const backgroundInformation: Section = {
        title: "Your background information",
        formSection: FormSectionsEnum.BackgroundInformation,
        component: BackgroundInformationView,
    };
    const declaration: Section = {
        title: "Declaration",
        formSection: FormSectionsEnum.Declaration,
        component: DeclarationView,
    };
    const creditReport: Section = {
        title: "Credit report",
        formSection: FormSectionsEnum.CreditReport,
        component: CreditReportView,
    };
    const supportingInformation: Section = {
        title: "Supporting information",
        formSection: FormSectionsEnum.SupportingInformation,
        component: SupportingInformationView,
    };

    const stepOneSections: Section[] = [personalDetails];
    const stepTwoSections: Section[] = [
        employmentDetails,
        incomeSavingsAndOutgoings,
        statements,
        propertyDetails,
        currentPropertyDetails,
        newPropertyDetails,
        backgroundInformation,
        declaration,
    ];
    // SW - removed 11550
    const stepThreeSections: Section[] = [creditReport, supportingInformation];
    //const stepThreeSections: Section[] = [creditReport];
    // if (!viewModel.model.requestedSupportingInfo) {
    //     stepThreeSections.push(supportingInformation);
    // }
    const [showMsgOne, setShowMsgOne] = useState(false);
    const [showMsgTwo, setShowMsgTwo] = useState(false);

    const [timeLock, setTimeLock] = useState(false);
    useEffect(() => {
        if (timeLock) return;
        calculateCurrentStep();
        setTimeout(() => setTimeLock(true), 5000);
    });

    const calculateCurrentStep = (): void => {
        let stepOneCanProceed = stepOneSections.every((section) => viewModel.applicantViewModels.every((a) => a.getSectionsCompleted[section.formSection] == true));
        let stepTwoCanProceed = stepTwoSections.every((section) => viewModel.applicantViewModels.every((a) => a.getSectionsCompleted[section.formSection] == true));

        if (!viewModel.model.isCompleted && !viewModel.model.isSubmitted) {
            if (stepOneCanProceed == true) {
                setShowStep1ProceedButton(false);
                setCurrentStep(ApplicationStepEnum.Step2);
            } else {
                setShowStep1ProceedButton(true);
                setCurrentStep(ApplicationStepEnum.Step1);
                return;
            }
            if (stepTwoCanProceed == true) {
                setShowStep2ProceedButton(false);
                setCurrentStep(ApplicationStepEnum.Step3);
            } else {
                setShowStep2ProceedButton(true);
                setCurrentStep(ApplicationStepEnum.Step2);
            }
        }
    };

    /**
     * This is the main function that is used to build the application form.
     * Add new a StepContainer to extend the form.
     */
    const applicationForm = () => {
        const [hasErrors, setHasErrors] = React.useState<boolean[]>(new Array(Object.keys(FormSectionsEnum).length / 2).fill(false));
        async function handleCheckSectionsErrors(): Promise<boolean> {
            console.log("handleCheckSectionsErrors()");
            let temp: boolean[] = [];
            for (const section of [...stepOneSections, ...stepTwoSections, ...stepThreeSections]) {
                let sectionHasError = false;

                let primaryApplicant = viewModel.getSortedApplicantViewModels.find((a) => a.getValue("primaryApplicant") === true);

                let hasOwnedPropertyInUKorAbroad = viewModel.getSortedApplicantViewModels.some(
                    (applicant) => applicant.personalDetailsViewModel.model.hasOwnedPropertyInUKorAbroad,
                );
                let skipCurrentPropertySection = section.formSection == FormSectionsEnum.CurrentPropertyDetails && hasOwnedPropertyInUKorAbroad == false;

                let checkCurrentPropertyApplicantTwo = (applicantId: string): boolean => {
                    if (section.formSection != FormSectionsEnum.CurrentPropertyDetails) return true;
                    if (isPrimaryApplicant(applicantId)) return true;
                    let primaryApplicant = getPrimaryApplicant();
                    if (primaryApplicant != undefined) {
                        return primaryApplicant.model.currentPropertyDetailsModel.sameForApplicantTwo ? false : true;
                    }
                    return false;
                };

                let checkNewPropertyApplicantTwo = (applicantId: string): boolean => {
                    if (section.formSection != FormSectionsEnum.NewPropertyDetails) return true;
                    if (isPrimaryApplicant(applicantId) == true) return true;
                    let primaryApplicant = getPrimaryApplicant();
                    if (primaryApplicant != undefined) {
                        return primaryApplicant.model.newPropertyDetailsModel.sameForApplicantTwo == true ? false : true;
                    }
                    return false;
                };
                const getPrimaryApplicant = (): ApplicantViewModel | undefined => {
                    let applicants = viewModel.applicantViewModels;
                    let primaryApplicant = applicants.find((a) => a.model && a.model.primaryApplicant);
                    return primaryApplicant;
                };
                const isPrimaryApplicant = (applicantId: string): boolean => {
                    let applicants = viewModel.applicantViewModels;
                    let firstApplicant = applicants.find((a) => a.model.primaryApplicant);
                    if (firstApplicant) {
                        if (applicantId == firstApplicant.model.id) return true;
                    }
                    return false;
                };

                if (!skipCurrentPropertySection) {
                    for (const applicant of viewModel.getSortedApplicantViewModels) {
                        let skipCurrentProperty = false;

                        if (section.formSection == FormSectionsEnum.CurrentPropertyDetails) {
                            skipCurrentProperty = !applicant.personalDetailsViewModel.getValue("hasOwnedPropertyInUKorAbroad");
                        }

                        let skipNewPropertyDetailsApplicantTwo =
                            section.formSection == FormSectionsEnum.NewPropertyDetails && checkNewPropertyApplicantTwo(applicant.model.id ? applicant.model.id : "") == false;

                        if (skipNewPropertyDetailsApplicantTwo) {
                            let viewModel = applicant.getViewModelByFormSectionEnum(section.formSection);
                            applicant.updateSectionCompleted(section.formSection, true);
                            temp[section.formSection] = false;

                            continue;
                        }

                        if (skipCurrentProperty == false) {
                            let viewModel = applicant.getViewModelByFormSectionEnum(section.formSection);
                            if (viewModel) {
                                let hasError = await viewModel.showUnfinishedFields();
                                if (hasError == true) {
                                    sectionHasError = true;
                                }
                            }
                        }
                    }
                }
                console.log("section: " + section.title + " has errors: " + sectionHasError);
                temp[section.formSection] = sectionHasError;
            }
            setHasErrors(temp);
            return hasErrors.some((error) => error == true);
        }
        return useObserver(() => (
            <>
                {/* Always show step 1 */}
                <StepContainer
                    // key={uniqueId("step-")}
                    showProceedButton={showStep1ProceedButton}
                    sections={stepOneSections}
                    step={ApplicationStepEnum.Step1}
                    applicationFormViewModel={viewModel}
                    accordionControls={accordionControls}
                    onProceedClick={() => {
                        setShowMsgOne(true);
                        setTimeLock(false);
                        setHasErrors(new Array(Object.keys(FormSectionsEnum).length / 2).fill(false));
                    }}
                    hasErrors={hasErrors}
                    handleCheckSectionsErrors={handleCheckSectionsErrors}
                />
                {showMsgOne && (
                    <Box style={{ marginTop: "15px" }}>
                        <Typography style={{ fontSize: "28px", fontWeight: "bold" }}>Thank you for adding your personal details</Typography>
                        <Typography variant="body2">
                            Before we can start the process of reviewing your application we need a few more details from you. Please take the time to fill in the sections added
                            for you below.
                        </Typography>
                        <hr style={{ margin: "30px 0px" }} />
                    </Box>
                )}
                {currentStep >= ApplicationStepEnum.Step2 && (
                    <StepContainer
                        showProceedButton={showStep2ProceedButton}
                        sections={stepTwoSections}
                        step={ApplicationStepEnum.Step2}
                        applicationFormViewModel={viewModel}
                        accordionControls={accordionControls}
                        onProceedClick={() => {
                            setShowMsgTwo(true);
                            setTimeLock(false);
                            setHasErrors(new Array(Object.keys(FormSectionsEnum).length / 2).fill(false));
                            saveStore.setIsSaved(true);
                            console.log("On proceed click.");
                        }}
                        hasErrors={hasErrors}
                        handleCheckSectionsErrors={handleCheckSectionsErrors}
                    />
                )}
                {showMsgTwo == true && currentStep >= ApplicationStepEnum.Step3 && (
                    <Box style={{ marginTop: "15px" }}>
                        <Typography style={{ fontSize: "28px", fontWeight: "bold" }}>Thank you for completing the above application form.</Typography>
                        <Typography variant="body2">Please upload your Credit Report and all supporting documents below.</Typography>
                        <hr style={{ margin: "30px 0px" }} />
                    </Box>
                )}
                {currentStep >= ApplicationStepEnum.Step3 && (
                    <StepContainer
                        showProceedButton={false}
                        sections={stepThreeSections}
                        step={ApplicationStepEnum.Step3}
                        applicationFormViewModel={viewModel}
                        accordionControls={accordionControls}
                        hasErrors={hasErrors}
                        handleCheckSectionsErrors={handleCheckSectionsErrors}
                    />
                )}

                {viewModel.getValue("isSubmitted") && (
                    <div>
                        <Typography variant={"body1"}>Thank you for submitting your application the sales team will be in contact with you in the next 10 working days.</Typography>
                        <Typography variant={"body1"}>
                            In the meantime if you have any questions then contact us on{" "}
                            <Link href={"tel:01442 292381"} style={{ color: theme.palette.hightownGreen.dark, textDecoration: "underline", fontWeight: "bold" }}>
                                01442 292381
                            </Link>
                        </Typography>
                    </div>
                )}

                <SaveView />
                <Box style={{ display: "flex", margin: "15px 0px" }}>
                    <StyledButton onClick={viewModel.save} disabled={!saveStore.isSaved}>
                        {saveStore.isSaved ? <>Save for later</> : <>Saving...</>}
                    </StyledButton>
                    <StyledButton
                        onClick={async () => {
                            let hasErrors = await handleCheckSectionsErrors();
                            if (!hasErrors) {
                                if (viewModel.applicantViewModels.every((a) => a.canSubmit)) {
                                    await viewModel.submit();
                                }
                            }
                        }}
                        disabled={!saveStore.isSaved}
                    >
                        {saveStore.isSaved ? <>Submit application</> : <>Saving...</>}
                    </StyledButton>
                </Box>
            </>
        ));
    };

    const lastSavedAndCurrentStatus = () => (
        <>
            <Box style={isMobile ? { display: "flex", alignItems: "center", flexDirection: "column", marginTop: "20px" } : {}}>
                <Typography variant={"h6"} style={{ fontWeight: "bold" }}>
                    Last saved: {viewModel.lastSaved}
                </Typography>
                <Typography variant={"h6"} style={{ fontWeight: "bold" }}>
                    Current status: {viewModel.statusName}
                </Typography>
            </Box>
            <SaveForLaterView />
        </>
    );
    const informationSection = () => (
        <>
            <Box style={{ margin: "30px 0px", padding: "0px 10px" }}>
                <Typography variant={"h4"} style={{ fontWeight: "bold", margin: "0px 0px 30px" }}>
                    Take a moment to complete this application form and you could be on your way to a new home
                </Typography>
                <Typography variant={"body2"} style={{ marginBottom: "10px" }}>
                    Your application form must be fully completed before we can consider it. If you are applying jointly, both applicants must complete this form.
                </Typography>

                {/* <Typography variant={"body2"} style={{ marginBottom: "10px" }}>
                    You cannot complete this application form unless you have registered with{" "}
                    <Link style={{ textDecoration: "underline", color: theme.palette.hightownBlue.light }} href={"https://www.helptobuyagent3.org.uk/"}>
                        helptobuyagent3.org.uk/
                    </Link>
                </Typography> */}

                <Typography variant={"body2"}>
                    If you’ve got any questions, {isMobile ? <br></br> : ""} call us on:{" "}
                    <Link href={"tel:01442 292381"} style={{ color: theme.palette.hightownGreen.dark, fontWeight: "bold" }}>
                        01442 292381
                    </Link>{" "}
                    {isMobile ? <br></br> : ""}
                    or email us at:{" "}
                    <Link href={"mailto: Sales@hightownha.org.uk"} style={{ color: theme.palette.hightownGreen.dark, textDecoration: "underline" }}>
                        Sales@hightownha.org.uk
                    </Link>
                </Typography>
            </Box>
        </>
    );
    const numberOfApplicants = () => (
        <Box style={{ margin: "30px 0px", padding: "0px 10px" }}>
            <Typography variant={"h6"} style={{ fontWeight: "bold", margin: "0px 0px 30px" }}>
                How many applicants are there?
            </Typography>
            <FormControl component="fieldset">
                <RadioGroup
                    aria-label="numberOfApplicants"
                    name="numberOfApplicants"
                    row
                    onChange={(e: any, val: string) => {
                        let applicantNumber = Number(val); // https://i.stack.imgur.com/PXxhB.png
                        if (applicantNumber < viewModel.numberOfApplicants) {
                            setTempApplicantNumber(applicantNumber);
                            setModalStatus(true);
                        } else {
                            viewModel.handleApplicantChange(applicantNumber);
                        }
                    }}
                >
                    <FormControlLabel
                        checked={viewModel.numberOfApplicants == 1}
                        control={
                            <Radio
                                checked={viewModel.numberOfApplicants == 1}
                                icon={<RadioSVG />}
                                checkedIcon={<RadioCheckedSVG />}
                                style={{ color: theme.palette.hightownBlue.light }}
                            />
                        }
                        label={<>One</>}
                        value={1}
                        disabled={viewModel.model.isSubmitted}
                    />
                    <FormControlLabel
                        checked={viewModel.numberOfApplicants == 2}
                        control={
                            <Radio
                                checked={viewModel.numberOfApplicants == 2}
                                icon={<RadioSVG />}
                                checkedIcon={<RadioCheckedSVG />}
                                style={{ color: theme.palette.hightownBlue.light }}
                            />
                        }
                        label={<>Two</>}
                        value={2}
                        disabled={viewModel.model.isSubmitted}
                    />
                </RadioGroup>
            </FormControl>
            <hr style={{ margin: "30px 0px" }} />
        </Box>
    );
    const pageEndInformation = () => (
        <>
            {" "}
            <hr style={{ margin: "30px 0px" }} />
            <Box display="flex" style={isMobile ? { flexDirection: "column" } : {}}>
                <Box style={isMobile ? { marginBottom: "10px" } : { marginRight: "10px" }}>
                    <Typography variant={"body1"} style={isMobile ? { fontSize: "16px" } : {}}>
                        If you have any questions please contact our sales department:
                    </Typography>
                </Box>
                <Box style={isMobile ? { marginBottom: "10px" } : { marginRight: "10px" }}>
                    <Typography style={isMobile ? { fontSize: "16px" } : {}} variant={"body1"}>
                        <Link href={"tel:01442 292381"} style={{ color: theme.palette.hightownGreen.dark, textDecoration: "underline", fontWeight: "bold" }}>
                            01442 292381
                        </Link>
                    </Typography>
                </Box>
                <Box style={isMobile ? { marginBottom: "10px" } : { marginRight: "10px" }}>
                    <Typography style={isMobile ? { fontSize: "16px" } : {}}>
                        <Link href={"mailto: Sales@hightownha.org.uk"} style={{ color: theme.palette.hightownGreen.dark, textDecoration: "underline" }}>
                            Sales@hightownha.org.uk
                        </Link>
                    </Typography>
                </Box>
            </Box>
        </>
    );

    const modals = () => (
        <>
            {modalStatus && (
                <DeleteApplicantModal
                    isActive={modalStatus}
                    confirmClose={() => {
                        viewModel.handleApplicantChange(tempApplicantNumber);
                        setModalStatus(false);
                    }}
                    onCancel={() => setModalStatus(false)}
                />
            )}
        </>
    );

    return useObserver(() => (
        <Box>
            <Box style={isMobile ? { padding: "10px 15px", width: "98vw" } : { padding: "40px 35px" }}>
                {viewModel.IsLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" style={{ marginTop: "100px", marginBottom: "100px" }}>
                        <Typography variant="h4">Loading your application</Typography>
                        <LinearProgress color="primary" style={{ width: "50%", marginTop: "15px" }} />
                    </Box>
                )}

                <span style={{ display: viewModel.IsLoading ? "none" : "block" }}>
                    {lastSavedAndCurrentStatus()}
                    {informationSection()}
                    {numberOfApplicants()}
                    {applicationForm()}
                    {pageEndInformation()}
                </span>
            </Box>
            {modals()}
        </Box>
    ));
};
